// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useId, type ReactNode } from 'react';

import styles from './Panel.module.css';

export interface PanelProps {
  /** The fixed header's caption; none = a panel without a header. */
  title?: string;
  /** In the header, after the title ("Rediger"). */
  action?: ReactNode;
  /** The title's id, for an `action` that describes itself by it. */
  headingId?: string;
  /** May shrink to the room left in its column; the body then scrolls under the fixed header. */
  fill?: boolean;
  children: ReactNode;
}

/**
 * One of the separate floating panels a `stacked` tile is made of
 * (`ModeView.stacked`): a fixed header over a body that scrolls when a `fill`
 * panel does not fit its column. The pattern for every titled floating panel
 * (Overblik's columns, Modelindstillinger): a `Panel` in a `stacked` tile,
 * never a title row inside a plain tile that scrolls away with the body.
 */
export function Panel({ title, action, headingId, fill, children }: PanelProps) {
  const generated = useId();
  const id = headingId ?? `${generated}-h`;
  return (
    <section className={fill ? `${styles.panel} ${styles.fill}` : styles.panel} aria-labelledby={title ? id : undefined}>
      {title && (
        <div className={styles.head}>
          <h3 id={id} className={styles.heading}>
            {title}
          </h3>
          {action}
        </div>
      )}
      <div className={styles.body}>{children}</div>
    </section>
  );
}
